import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { ClientStatus } from '../../context/ClientContext';
import { useUser } from '../../context/UserContext';
import { Badge, Button, Dropdown, Icon, Input, SidePanel } from '../ui';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Prospect', label: 'Prospect' },
  { value: 'Paused', label: 'Paused' },
  { value: 'Archived', label: 'Archived' },
];

/**
 * Add Client slide-over — same fields and Supabase insert as before,
 * restyled on the SidePanel shell.
 */
export default function CreateClientModal({ isOpen, onClose, onCreated }: CreateClientModalProps) {
  const { user } = useUser();
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    industry: '',
    status: 'Active' as ClientStatus,
    tags: [] as string[],
  });
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addTag = () => {
    const tag = newTag.trim();
    if (tag && !newClient.tags.includes(tag)) {
      setNewClient({ ...newClient, tags: [...newClient.tags, tag] });
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setNewClient({ ...newClient, tags: newClient.tags.filter((t) => t !== tag) });
  };

  const handleCreateClient = async () => {
    if (!newClient.name.trim()) {
      setError('Client name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (!user?.agency_id) throw new Error('Your account has no agency');
      const { error: insertError } = await supabase
        .from('clients')
        .insert([{
          name: newClient.name,
          email: newClient.email || null,
          phone: newClient.phone || null,
          industry: newClient.industry || null,
          status: newClient.status,
          tags: newClient.tags,
          account_owner_id: user?.id,
          account_owner_name: user?.name,
          agency_id: user.agency_id,
          start_date: new Date().toISOString(),
        }]);

      if (insertError) throw insertError;

      setNewClient({ name: '', email: '', phone: '', industry: '', status: 'Active', tags: [] });
      setNewTag('');
      onClose();
      onCreated?.();
    } catch (err) {
      console.error('Error creating client:', err);
      setError('Failed to create client. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateClient();
    }
  };

  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      eyebrow="New Client"
      title="Add Client"
      footer={
        <>
          <Button
            icon="plus"
            onClick={handleCreateClient}
            disabled={isSubmitting || !newClient.name.trim()}
          >
            {isSubmitting ? 'Creating...' : 'Add Client'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error ? (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400, #E5484D)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        ) : null}

        <Input
          label="Client Name"
          icon="building-2"
          placeholder="Acme Industries..."
          value={newClient.name}
          onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
          onKeyDown={submitOnEnter}
          required
        />

        <Input
          label="Email"
          icon="mail"
          type="email"
          placeholder="name@company.com"
          value={newClient.email}
          onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
          onKeyDown={submitOnEnter}
        />

        <Input
          label="Phone"
          type="tel"
          placeholder="Phone number..."
          value={newClient.phone}
          onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
          onKeyDown={submitOnEnter}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Input
            label="Industry"
            placeholder="Industry..."
            value={newClient.industry}
            onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })}
            onKeyDown={submitOnEnter}
          />
          <Dropdown
            label="Status"
            options={STATUS_OPTIONS}
            value={newClient.status}
            onChange={(e) => setNewClient({ ...newClient, status: e.target.value as ClientStatus })}
          />
        </div>

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Tags</div>
          {newClient.tags.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {newClient.tags.map((tag) => (
                <Badge key={tag} tone="neutral">
                  {tag}
                  <button
                    type="button"
                    className="pressable"
                    aria-label={`Remove ${tag}`}
                    onClick={() => removeTag(tag)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: 0,
                      marginLeft: '2px',
                      border: 'none',
                      background: 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon name="x" size={10} />
                  </button>
                </Badge>
              ))}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <Input
                placeholder="Add a tag..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
            </div>
            <Button type="button" variant="secondary" onClick={addTag}>Add</Button>
          </div>
        </div>

        <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          Contacts and platform IDs can be added from the client page after creation.
        </p>
      </div>
    </SidePanel>
  );
}
